/* oxlint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-to-interactive-role, jsx-a11y/prefer-tag-over-role -- role=combobox/listbox/option/status is the ARIA APG combobox pattern; keyboard is on the input, not the option */
import {
  useEffect,
  useRef,
  useState,
  useId,
  type FocusEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react';

import { cn } from '../lib/cn';
import { Field } from './field';
import { InlineError } from './inline-error';
import { Input } from './input';

export type SearchComboboxStatus = 'idle' | 'loading' | 'results' | 'empty' | 'error';

export interface SearchComboboxOption {
  value: string;
  label: ReactNode;
  description?: ReactNode;
}

const popupBaseClassName =
  'border-line bg-surface z-popover absolute top-full mt-1 w-full rounded-lg border shadow-sm';

export interface SearchComboboxProps {
  label: ReactNode;
  inputValue: string;
  onInputChange: (value: string) => void;
  /**
   * 배타적 dropdown 상태(E14 §7.1). 호출자가 `입력값 === debounce된 질의값`으로
   * 계산해서 넘겨야 입력 중에 이전 질의 결과가 현재 결과처럼 보이지 않는다.
   */
  status: SearchComboboxStatus;
  options: ReadonlyArray<SearchComboboxOption>;
  onSelect: (value: string) => void;
  onRetry?: () => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** @default '검색 중…' */
  loadingText?: ReactNode;
  /** @default '검색 결과가 없어요.' */
  emptyText?: ReactNode;
  /** @default '목록을 불러오지 못했어요.' */
  errorText?: ReactNode;
  /** @default '다시 시도' */
  retryLabel?: ReactNode;
}

/**
 * 서버 검색 combobox — E14 §7.1 · D87. 입력값(`inputValue`)과 debounce된 질의값의
 * 분리, 요청 상태별 배타적 표시는 호출자가 `status`로 넘긴다. 이 컴포넌트는
 * role·aria 배선, 키보드(↑↓ Enter Esc) + 마우스, 선택·focus 이탈 후 초기화만 맡는다.
 */
export function SearchCombobox({
  label,
  inputValue,
  onInputChange,
  status,
  options,
  onSelect,
  onRetry,
  placeholder,
  disabled = false,
  className,
  loadingText = '검색 중…',
  emptyText = '검색 결과가 없어요.',
  errorText = '목록을 불러오지 못했어요.',
  retryLabel = '다시 시도',
}: SearchComboboxProps) {
  const generatedId = useId();
  const listboxId = `${generatedId}-listbox`;
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const optionRefs = useRef<Array<HTMLLIElement | null>>([]);
  const expanded = isOpen && status !== 'idle';
  const showListbox = expanded && status === 'results' && options.length > 0;

  useEffect(() => {
    setActiveIndex(-1);
  }, [options, status]);

  useEffect(() => {
    if (showListbox && activeIndex >= 0) {
      optionRefs.current[activeIndex]?.scrollIntoView?.({ block: 'nearest' });
    }
  }, [activeIndex, showListbox]);

  function select(value: string) {
    onSelect(value);
    setIsOpen(false);
    setActiveIndex(-1);
  }

  function close() {
    setIsOpen(false);
    setActiveIndex(-1);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Escape') {
      close();
      return;
    }
    if (!showListbox) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => (index + 1) % options.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => (index <= 0 ? options.length - 1 : index - 1));
    } else if (event.key === 'Enter' && activeIndex >= 0) {
      event.preventDefault();
      select(options[activeIndex]!.value);
    }
  }

  function handleBlur(event: FocusEvent<HTMLDivElement>) {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      close();
    }
  }

  let popup: ReactNode = null;
  if (expanded) {
    if (status === 'loading') {
      popup = (
        <div
          id={listboxId}
          role="status"
          className={cn(popupBaseClassName, 'px-row py-row text-ink-muted text-sm')}
        >
          {loadingText}
        </div>
      );
    } else if (status === 'error') {
      popup = (
        <div id={listboxId} className={cn(popupBaseClassName, 'px-row py-row')}>
          <InlineError onRetry={onRetry} retryLabel={retryLabel}>
            {errorText}
          </InlineError>
        </div>
      );
    } else if (status === 'empty') {
      popup = (
        <div
          id={listboxId}
          role="status"
          className={cn(popupBaseClassName, 'px-row py-row text-ink-muted text-sm')}
        >
          {emptyText}
        </div>
      );
    } else if (status === 'results' && options.length > 0) {
      popup = (
        <ul
          id={listboxId}
          role="listbox"
          tabIndex={-1}
          aria-label={typeof label === 'string' ? `${label} 결과` : undefined}
          className={cn(popupBaseClassName, 'max-h-60 overflow-auto p-1')}
        >
          {options.map((option, index) => {
            const optionId = `${listboxId}-${index}`;
            const active = index === activeIndex;
            return (
              <li
                id={optionId}
                key={option.value}
                ref={(element) => {
                  optionRefs.current[index] = element;
                }}
                role="option"
                tabIndex={-1}
                aria-selected={active}
                aria-disabled={disabled}
                className={cn(
                  'text-ink px-row py-snug w-full cursor-pointer rounded-md text-left text-sm',
                  active ? 'bg-highlight' : 'hover:bg-highlight',
                )}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  if (!disabled) select(option.value);
                }}
              >
                {option.label}
                {option.description ? (
                  <span className="text-ink-muted mt-0.5 block text-xs">{option.description}</span>
                ) : null}
              </li>
            );
          })}
        </ul>
      );
    }
  }

  return (
    <Field label={label} className={className}>
      {(control) => (
        <div className="relative flex flex-col" onBlur={handleBlur}>
          <Input
            {...control}
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={expanded}
            aria-controls={listboxId}
            aria-activedescendant={
              showListbox && activeIndex >= 0 ? `${listboxId}-${activeIndex}` : undefined
            }
            value={inputValue}
            disabled={disabled}
            onFocus={() => setIsOpen(true)}
            onClick={() => setIsOpen(true)}
            onChange={(event) => {
              onInputChange(event.target.value);
              setIsOpen(true);
              setActiveIndex(-1);
            }}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
          />
          {popup}
        </div>
      )}
    </Field>
  );
}

/**
 * 입력값과 debounce된 질의값을 분리한다. `query === debouncedQuery`로
 * `SearchCombobox`의 `status`를 계산하면 입력 중에 이전 결과가 보이지 않는다.
 */
export function useDebouncedQuery(query: string, delayMs: number): string {
  const [debouncedQuery, setDebouncedQuery] = useState(query);

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedQuery(query), delayMs);
    return () => window.clearTimeout(timeout);
  }, [query, delayMs]);

  return debouncedQuery;
}
